import { sendNotification, useAsyncCache, useRouteParams, useSignalrTopic, useTranslation } from '@msdk/common'
import { useComponentModal } from '@msdk/forms'
import { UserAuthenticationNGID } from 'components/UserAuthenticationNGID/UserAuthenticationNGID'
import { useAutoLogOff, useNotifyDefaultClinic } from 'hooks'
import { useNotifyLastLogin } from 'hooks/useNotifyLastLogin'
import { useUserDialysisMachineSignedIn } from 'hooks/useUserDialysisMachineSignedIn'
import { ReactNode, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

interface Props {
  children?: ReactNode
}

/**
 * This component is a convenient place to initialize application hooks.
 * Here you can initialize hooks that have some logic that should run on startup.
 * Different from the AppInitialized component here all the application providers are available
 * to use, but since they are already initialized it is not possible to block the shell here,
 * so this component should only contain initialization that can run without blocking.
 */
export function ContentIntialized(props: Props) {
  const cache = useAsyncCache()
  const t = useTranslation()
  const routeParam = useRouteParams()
  const { search } = useLocation()
  const dialogModal = useComponentModal()
  const openNGID = search.includes('openNGID')
  const { onClose: onCloseNGID, updateStartIndex } = useUserDialysisMachineSignedIn(openNGID)

  useAutoLogOff()
  useNotifyDefaultClinic()
  useNotifyLastLogin()

  useSignalrTopic('system.measurement-disease-code-imported', () => {
    if (routeParam.entityName === 'DISEASE_CODE' && routeParam.moduleName?.includes('DISEASE_CODES_VIEW')) {
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    }
  })

  useSignalrTopic('system.measurement-unit-convertions-changed', () => {
    const msg = t(
      'MSG_MEASUREMENT_UNIT_CONVERTIONS_CHANGED',
      'Measurement unit conversions have changed. Page will be reloaded in few seconds.',
    )

    // to avoid be overlapped by other notifications
    setTimeout(() => {
      sendNotification({ message: msg, status: 'warning', waitUserAck: true })
    }, 500)

    setTimeout(() => {
      cache.invalidateFrameworkCache('MeasurementUnit')
      window.location.reload()
    }, 5000)
  })

  useEffect(() => {
    if (search.includes('openNGID')) {
      const fetchdata = async () => {
        const startIndex = await updateStartIndex()
        dialogModal
          .createModal(<UserAuthenticationNGID startIndex={startIndex} />, onCloseNGID, {
            preventClickOutside: false,
            preventEscapeFromClosing: false,
          })
          .open()
        const url = new URL(window.location.href)
        url.searchParams.delete('openNGID')
        window.history.replaceState({}, '', url.toString())
      }
      fetchdata().catch(console.error)
    }
  }, [search])

  return props.children
}
